import { Fragment, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

export default function SendReferralModal({ isOpen, onClose, toMember, onSubmit }) {
  const [formData, setFormData] = useState({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    company: '',
    notes: '',
  });

  const canSubmit = useMemo(() => {
    return Boolean(String(formData.contactName).trim());
  }, [formData.contactName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!toMember?.id) return;

    await onSubmit({
      toMemberId: toMember.id,
      contactName: String(formData.contactName).trim(),
      contactEmail: String(formData.contactEmail).trim() || null,
      contactPhone: String(formData.contactPhone).trim() || null,
      company: String(formData.company).trim() || null,
      notes: String(formData.notes).trim() || null,
    });

    setFormData({ contactName: '', contactEmail: '', contactPhone: '', company: '', notes: '' });
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="px-6 py-5">
                  <Dialog.Title className="text-xl font-playfair-sans text-NBNA-primary">
                    Send Referral
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    To: <span className="font-medium">{toMember?.businessName || toMember?.name}</span>
                  </p>

                  <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ref-contactName">Referral contact name</label>
                      <input
                        id="ref-contactName"
                        type="text"
                        className="input-field w-full"
                        value={formData.contactName}
                        onChange={(e) => setFormData((p) => ({ ...p, contactName: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ref-contactEmail">Contact email (optional)</label>
                        <input
                          id="ref-contactEmail"
                          type="email"
                          className="input-field w-full"
                          value={formData.contactEmail}
                          onChange={(e) => setFormData((p) => ({ ...p, contactEmail: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ref-contactPhone">Contact phone (optional)</label>
                        <input
                          id="ref-contactPhone"
                          type="tel"
                          className="input-field w-full"
                          value={formData.contactPhone}
                          onChange={(e) => setFormData((p) => ({ ...p, contactPhone: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ref-company">Company (optional)</label>
                      <input
                        id="ref-company"
                        type="text"
                        className="input-field w-full"
                        value={formData.company}
                        onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ref-notes">Notes (optional)</label>
                      <textarea
                        id="ref-notes"
                        className="input-field w-full"
                        rows={4}
                        value={formData.notes}
                        onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                        placeholder="Context, preferred intro, timing, etc."
                      />
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button type="button" className="btn-secondary" onClick={onClose}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary" disabled={!canSubmit}>
                        Send Referral
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
